/**
 * Dashboard API Hooks
 * React hooks for dashboard data endpoints
 * Based on dashboard-controller from admin-docs.json
 */

import { useCallback } from 'react';
import { useFetch, buildUrl } from './useApi';
import { ADMIN_API_BASE_URL } from '../config';
import { getAuthToken } from './useAdminRefactored';
import { formatDateToApiDateTime } from '@/lib/utils';
import type {
  DateRangeParams,
  TodayTripsResponse,
  TripsBetweenDatesResponse,
  TodayRevenueResponse,
  RevenueBetweenDatesResponse,
  TodayGMVResponse,
  GMVBetweenDatesResponse,
  OnDutyDriverListResponse,
  ActiveRidesResponse,
  RideHistoryItem,
} from './dashboardTypes';
import type { ApiResponse, PagedData } from './types';

const API_BASE_URL_ADMIN = ADMIN_API_BASE_URL;
/**
 * Get authentication headers
 */
function getAuthHeaders(): Record<string, string> {
  const token = getAuthToken();
  // return token ? { Authorization: `Bearer ${token}` } : {};
  return {};
}

// ============================================================================
// Trip Endpoints
// ============================================================================

/**
 * Hook to fetch today's trips data
 * GET /dashboard/trips/today
 */
export function useTodayTrips(
  options: {
    enabled?: boolean;
    onSuccess?: (data: TodayTripsResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(API_BASE_URL_ADMIN, '/dashboard/trips/today');

  return useFetch<TodayTripsResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch trips data between dates
 * GET /dashboard/trips/between-dates
 */
export function useTripsBetweenDates(
  params: DateRangeParams,
  options: {
    enabled?: boolean;
    onSuccess?: (data: TripsBetweenDatesResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(API_BASE_URL_ADMIN, '/dashboard/trips/between-dates', {
    from: params.from,
    to: params.to,
  });
  
  return useFetch<TripsBetweenDatesResponse>(url, {
    enabled: options.enabled !== false && !!params.from && !!params.to,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

// ============================================================================
// Revenue Endpoints
// ============================================================================

/**
 * Hook to fetch today's revenue data
 * GET /dashboard/revenue/today
 */
export function useTodayRevenue(
  options: {
    enabled?: boolean;
    onSuccess?: (data: TodayRevenueResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(API_BASE_URL_ADMIN, '/dashboard/revenue/today');

  return useFetch<TodayRevenueResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch revenue data between dates
 * GET /dashboard/revenue/between-dates
 */
export function useRevenueBetweenDates(
  params: DateRangeParams,
  options: {
    enabled?: boolean;
    onSuccess?: (data: RevenueBetweenDatesResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(API_BASE_URL_ADMIN, '/dashboard/revenue/between-dates', {
    from: params.from,
    to: params.to,
  });
  
  return useFetch<RevenueBetweenDatesResponse>(url, {
    enabled: options.enabled !== false && !!params.from && !!params.to,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

// ============================================================================
// GMV Endpoints
// ============================================================================

/**
 * Hook to fetch today's GMV (Gross Merchandise Value) data
 * GET /dashboard/gmv/today
 */
export function useTodayGMV(
  options: {
    enabled?: boolean;
    onSuccess?: (data: TodayGMVResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(API_BASE_URL_ADMIN, '/dashboard/gmv/today');
  
  return useFetch<TodayGMVResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch GMV data between dates
 * GET /dashboard/gmv/between-dates
 */
export function useGMVBetweenDates(
  params: DateRangeParams,
  options: {
    enabled?: boolean;
    onSuccess?: (data: GMVBetweenDatesResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(API_BASE_URL_ADMIN, '/dashboard/gmv/between-dates', {
    from: params.from,
    to: params.to,
  });
  
  return useFetch<GMVBetweenDatesResponse>(url, {
    enabled: options.enabled !== false && !!params.from && !!params.to,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

// ============================================================================
// Driver/Rider Status Endpoints
// ============================================================================

/**
 * Hook to fetch list of on-duty drivers
 * GET /dashboard/on-duty-list
 */
export function useOnDutyDriverList(
  options: {
    enabled?: boolean;
    onSuccess?: (data: OnDutyDriverListResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(API_BASE_URL_ADMIN, '/dashboard/on-duty-list');
  
  return useFetch<OnDutyDriverListResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

/**
 * Hook to fetch list of active rides
 * GET /dashboard/active-rides
 */
export function useActiveRides(
  options: {
    enabled?: boolean;
    onSuccess?: (data: ActiveRidesResponse) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  const url = buildUrl(API_BASE_URL_ADMIN, '/dashboard/active-rides');

  return useFetch<ActiveRidesResponse>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError,
  });
}

// ============================================================================
// Ride History Endpoint (for calculating revenue from individual rides)
// ============================================================================

/**
 * Hook to fetch ride history for today
 * GET /reports/ride-history
 * Used to calculate revenue from individual ride fares
 */
export function useTodayRideHistory(
  options: {
    enabled?: boolean;
    onSuccess?: (data: ApiResponse<PagedData<RideHistoryItem>>) => void;
    onError?: (error: Error) => void;
  } = {}
) {
  // Get today's date range (API format: 2026-01-25T00:00:00)
  const getTodayDateRange = () => {
    const now = new Date();
    const startOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 0, 0, 0, 0);
    const endOfToday = new Date(now.getFullYear(), now.getMonth(), now.getDate(), 23, 59, 59, 999);
    return {
      from: formatDateToApiDateTime(startOfToday),
      to: formatDateToApiDateTime(endOfToday),
    };
  };

  const dateRange = getTodayDateRange();
  // API expects 1-based page and optional sort as JSON array string (matches ride-history curl)
  const url = buildUrl(API_BASE_URL_ADMIN, '/reports/ride-history', {
    from: dateRange.from,
    to: dateRange.to,
    page: 1,
    size: 1000, // Get all rides for today
  });
  
  return useFetch<ApiResponse<PagedData<RideHistoryItem>>>(url, {
    enabled: options.enabled !== false,
    headers: getAuthHeaders(),
    onSuccess: options.onSuccess,
    onError: options.onError ? options.onError : (error) => {
      // Silently ignore 404 and 500 errors - endpoint may not be implemented or may have issues
      if (!error.message.includes('404') && !error.message.includes('500')) {
        console.error('Failed to load ride history:', error);
      }
    },
  });
}
